'use client';

import { useState } from 'react';
import { DownloadIcon, FileIcon, LoaderPinwheelIcon, TrashIcon, UploadIcon } from 'lucide-react';
import { FolderContent } from 'sbx-lib-ts';
import { cn } from 'src/lib/utils';
import { Button } from '../ui/button';
import { useTranslate } from 'sbx-crm-menu-ui';
import { downloadFile as downloadTextFile } from 'src/utils';
import { downloadFile as downloadFileService } from 'src/services/AssetService';

interface FileUploadProps {
    files: File[];
    sbxFiles: FolderContent[];
    onFilesChange: (files: File[]) => void;
    onDeleteFile: (sbxFile: FolderContent) => Promise<void>;
    label?: React.ReactNode;
    accept?: string;
    maxFiles?: number;
    className?: string;
    disabled?: boolean;
}

export const FileUpload = ({
    files,
    sbxFiles,
    onFilesChange,
    onDeleteFile,
    label,
    accept,
    maxFiles,
    className,
    disabled = false,
}: FileUploadProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
    const { t: c } = useTranslate('common');

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length > 0) {
            const newFiles = maxFiles ? [...files, ...selectedFiles].slice(0, maxFiles) : [...files, ...selectedFiles];
            onFilesChange(newFiles);
        }
        // Reset input value to allow selecting the same file again
        e.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (disabled) return;

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            const newFiles = maxFiles ? [...files, ...droppedFiles].slice(0, maxFiles) : [...files, ...droppedFiles];
            onFilesChange(newFiles);
        }
    };

    const handleDelete = async (index: number, isSbx?: boolean) => {
        setDeletingIndex(index);
        try {
            if (isSbx) {
                await onDeleteFile(sbxFiles[index]);
            }
            const newFiles = files.filter((_, i) => i !== index);
            onFilesChange(newFiles);
        } finally {
            setDeletingIndex(null);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const splitNameAndExt = (filename: string): { base: string; ext: string } => {
        const lastDot = filename.lastIndexOf('.');
        if (lastDot === -1) return { base: filename, ext: 'txt' };
        return { base: filename.substring(0, lastDot), ext: filename.substring(lastDot + 1) };
    };

    const handleDownloadLocal = async (file: File) => {
        try {
            const text = await file.text();
            const { base, ext } = splitNameAndExt(file.name);
            downloadTextFile(text, base, ext || 'txt');
        } catch (e) {
            // noop
        }
    };

    const handleDownloadSbx = async (file: FolderContent) => {
        try {
            // get temp url then fetch its content as a Blob (supports images and binaries)
            const key: string = (file as any).key || '';
            if (!key) return;
            const res = await downloadFileService(key);
            if (res?.success && res.item?.url) {
                const resp = await fetch(res.item.url);
                const blob = await resp.blob();
                const objectUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                const filename = (res.item.name || file.name || 'download').toString();
                link.href = objectUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(objectUrl);
            }
        } catch (e) {
            // noop
        }
    };

    return (
        <div className={cn('space-y-2', className)}>
            {label && (
                <label className="font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {label}
                </label>
            )}

            {/* Upload Area */}
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                    'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer',
                    isDragging && !disabled
                        ? 'border-primary bg-primary/5'
                        : 'border-muted-foreground/25 hover:border-muted-foreground/50',
                    disabled && 'opacity-50 cursor-not-allowed'
                )}
            >
                <input
                    type="file"
                    onChange={handleFileChange}
                    accept={accept}
                    multiple={!maxFiles || maxFiles > 1}
                    disabled={disabled}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    id="file-upload-input"
                />
                <div className="flex flex-col items-center justify-center text-center">
                    <div className="flex size-10 items-center justify-center rounded-md bg-muted text-muted-foreground mb-2">
                        <UploadIcon size={20} />
                    </div>
                    <p className="font-medium text-sm mb-1">
                        {files.length > 0 ? c('add_more_files') : c('upload_files')}
                    </p>
                    <p className="text-muted-foreground text-xs">{c('drag_and_drop_or_browse')}</p>
                    {maxFiles && (
                        <p className="text-muted-foreground text-xs mt-1">
                            {c('max')} {maxFiles} {c('file')}
                            {maxFiles > 1 ? 's' : ''}
                        </p>
                    )}
                </div>
            </div>

            {/* Files List */}
            <div
                aria-expanded={!!files.length && !!sbxFiles.length}
                className="grid grid-cols-1 gap-2 aria-expanded:grid-cols-2"
            >
                {files.length > 0 && (
                    <div className="space-y-2 mt-4">
                        <p className="font-medium text-sm">
                            {c('uploaded_files')} ({files.length})
                        </p>
                        <div className="space-y-2">
                            {files.map((file, index) => (
                                <div
                                    key={`${file.name}-${index}`}
                                    className="flex items-center justify-between gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-accent/50"
                                >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="flex size-8 items-center justify-center rounded bg-muted text-muted-foreground flex-shrink-0">
                                            <FileIcon size={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{file.name}</p>
                                            <p className="text-muted-foreground text-xs">{formatFileSize(file.size)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDownloadLocal(file)}
                                            disabled={disabled}
                                            className="flex-shrink-0 h-8 w-8"
                                            title={c('download')}
                                        >
                                            <DownloadIcon size={16} />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(index)}
                                            disabled={deletingIndex === index || disabled}
                                            className="flex-shrink-0 h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                            title={c('delete')}
                                        >
                                            {deletingIndex === index ? (
                                                <LoaderPinwheelIcon className="animate-spin" />
                                            ) : (
                                                <TrashIcon size={16} />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {sbxFiles.length > 0 && (
                    <div className="space-y-2 mt-4">
                        <p className="font-medium text-sm">
                            {c('sbx_files')} ({sbxFiles.length})
                        </p>
                        <div className="space-y-2">
                            {sbxFiles.map((file, index) => (
                                <div
                                    key={`${file.name}-${index}`}
                                    className="flex items-center justify-between gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-accent/50"
                                >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="flex size-8 items-center justify-center rounded bg-muted text-muted-foreground flex-shrink-0">
                                            <FileIcon size={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{file.name}</p>
                                            <p className="text-muted-foreground text-xs">{formatFileSize(file.size)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDownloadSbx(file)}
                                            disabled={disabled}
                                            className="flex-shrink-0 h-8 w-8"
                                            title={c('download')}
                                        >
                                            <DownloadIcon size={16} />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(index, true)}
                                            disabled={deletingIndex === index || disabled}
                                            className="flex-shrink-0 h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                            title={c('delete')}
                                        >
                                            {deletingIndex === index ? (
                                                <LoaderPinwheelIcon className="animate-spin" />
                                            ) : (
                                                <TrashIcon size={16} />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
